// ─── Public Status Page — one row of the day grid ────────────────────
//
// A single component's (or the page's) uptime strip: an optional label, the
// day cells, and a screen-reader-only list of the days that were NOT normal.
//
// MEMOISED, and every prop is a primitive or a stable callback, because this is
// the component the page has ~28 of and the poll loop re-renders its parent
// every 15–60 s. With the memo in place, a poll that returns an identical
// payload re-renders exactly ZERO rows — and a payload that changed one
// component re-renders that one row. Nothing about the hover/keyboard cursor is
// passed in for the same reason: the cursor is drawn by the portalled tooltip,
// so moving it can never invalidate 90 cells (see HistoryGrid's comment on the
// delegated pointer handling).
//
// THE CELLS ARE INERT: `aria-hidden` spans with no listener, no title attribute,
// no tabindex. The row's accessible surface is the strip itself — one
// `role="img"` with a Danish summary — plus the hidden list below it. 90
// focusable cells per component would mean 2,500 tab stops on a page people open
// because something is already wrong.

import { memo, type CSSProperties, type FocusEvent, type KeyboardEvent } from 'react';
import { STATUS_COMPONENT_STATUSES, type StatusComponentStatus } from '@pcp/shared';

import { dayFromHistoryIndex, fmtDate } from '../lib/format';
import { NO_DATA_LEVEL, levelForStatus, statusForHistoryChar, INFO_LEVEL } from '../lib/levels';
import { VisuallyHidden } from '../ui/VisuallyHidden';

// The only copy on this page that does not come from ../i18n/da.ts: the grid's
// accessible summary needs a day noun with Danish plural agreement ("1 dag" /
// "2 dage"), and i18n/da.ts is owned by the page foundation. Kept here, next to
// its only use, and deliberately NOT locale-switched — the page is Danish-only
// (its NUMBERS and DATES still follow the reader's own regional settings, via
// lib/format).
const DAY_SINGULAR = 'dag';
const DAY_PLURAL = 'dage';

export interface HistoryGridRowProps {
  /** Row identity, echoed onto every cell as `data-c` for the delegated handler. */
  rowId: string;
  /** The component name / page label — the strip's accessible name. */
  label: string;
  /** Render the label as a visible column (the parent reserves LABEL_W for it). */
  showLabel: boolean;
  /** Narrow container: the label sits ABOVE the strip instead of beside it. */
  stacked: boolean;
  /** px width of the label column when it is beside the strip. */
  labelWidth: number;
  /** The VISIBLE window of the history string, oldest → newest. */
  history: string;
  /** The VISIBLE window of the information mask, sliced in step with `history`.
   *  '1' = a notice applied that day. Absent on the page-wide bar, deliberately —
   *  announcements belong on the individual system bars only. */
  info?: string;
  /** ABSOLUTE index → that day's href, or null for a day with nothing to show.
   *  Omit entirely and every cell stays the inert span it has always been. */
  hrefForCell?: (index: number) => string | null;
  /** Index of `history[0]` within the full history — cells carry absolute indices. */
  offset: number;
  /** YYYY-MM-DD of the FULL history's index 0 (`summary.historyStartDate`). */
  historyStartDate: string;
  size: 'sm' | 'lg';
  onStripFocus: (event: FocusEvent<HTMLDivElement>) => void;
  onStripBlur: () => void;
  onStripKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
}

/** Danish plural agreement for a day count. */
function days(count: number): string {
  return `${count} ${count === 1 ? DAY_SINGULAR : DAY_PLURAL}`;
}

interface Breakdown {
  /** "87 dage normal, 2 dage nedsat ydeevne, 1 dag driftsforstyrrelse" */
  summary: string;
  /** The non-normal days, as real text for a screen reader. Usually 0–5 items. */
  events: Array<{ key: string; text: string }>;
}

/**
 * Count the window by status, in severity order, and collect the days that were
 * not fully available. Both outputs are derived from the props alone, so they
 * ride the memo: they are recomputed only when the row's data actually changes.
 */
function describe(history: string, offset: number, historyStartDate: string, label: string): Breakdown {
  const counts = new Map<StatusComponentStatus | null, number>();
  const events: Array<{ key: string; text: string }> = [];

  for (let i = 0; i < history.length; i += 1) {
    const status = statusForHistoryChar(history.charAt(i));
    counts.set(status, (counts.get(status) ?? 0) + 1);
    if (status && status !== 'available') {
      const day = dayFromHistoryIndex(historyStartDate, offset + i);
      events.push({
        key: `${offset + i}`,
        text: `${fmtDate(day)}: ${levelForStatus(status).label}`,
      });
    }
  }

  const parts: string[] = [];
  for (const status of STATUS_COMPONENT_STATUSES) {
    const count = counts.get(status) ?? 0;
    if (count > 0) parts.push(`${days(count)} ${levelForStatus(status).label.toLowerCase()}`);
  }
  const noData = counts.get(null) ?? 0;
  if (noData > 0) parts.push(`${days(noData)} ${NO_DATA_LEVEL.label.toLowerCase()}`);

  return { summary: parts.length ? `${label}: ${parts.join(', ')}` : label, events };
}

export const HistoryGridRow = memo(function HistoryGridRow({
  rowId,
  label,
  showLabel,
  stacked,
  labelWidth,
  history,
  info,
  hrefForCell,
  offset,
  historyStartDate,
  size,
  onStripFocus,
  onStripBlur,
  onStripKeyDown,
}: HistoryGridRowProps) {
  const { summary, events } = describe(history, offset, historyStartDate, label);

  return (
    <div className={`sg-row${stacked ? ' sg-row--stacked' : ''}`}>
      {showLabel && (
        <div className="sg-row-label" style={stacked ? undefined : { width: labelWidth }}>
          {label}
        </div>
      )}
      <div
        className={`sg-strip${size === 'lg' ? ' sg-strip--lg' : ''}`}
        // The cell count drives the grid template, so the strip always fills its
        // track exactly — no trailing gap, no cell narrower than the rest.
        style={{ ['--sg-days' as string]: String(history.length) } as CSSProperties}
        role="img"
        aria-label={summary}
        tabIndex={0}
        data-c={rowId}
        onFocus={onStripFocus}
        onBlur={onStripBlur}
        onKeyDown={onStripKeyDown}
      >
        {Array.from(history, (char, index) => {
          const status = statusForHistoryChar(char);
          // A notice paints ONLY where the day is otherwise normal. If something
          // was actually wrong that day the fault keeps the cell — never hide a
          // real problem behind an announcement.
          const marked = info?.[index] === '1' && status === 'available';
          const level = marked ? INFO_LEVEL : levelForStatus(status);
          const absolute = offset + index;
          const href = hrefForCell?.(absolute) ?? null;
          const cls = `sg-cell${level === NO_DATA_LEVEL ? ' sg-cell--nodata' : ''}${href ? ' sg-cell--open' : ''}`;
          // The ONE per-cell payload: its colour, as a token reference.
          // `fill`, not `color` — a day cell is a solid block, and the block
          // tier is where the C&B brand colours live (lib/levels.ts).
          const style = { ['--sg-c' as string]: level.fill } as CSSProperties;
          // A day worth opening becomes a REAL anchor — this page's rule is that
          // everything navigable is an <a href>, so middle-click, ⌘-click and
          // "copy link address" all work. It stays tabIndex={-1} + aria-hidden:
          // the strip is the single tab stop and the accessible unit (90 cells
          // per component would be thousands of tab stops), and the keyboard
          // path is Enter on the strip plus the linked day list below it.
          return href ? (
            <a
              key={absolute}
              className={cls}
              style={style}
              href={href}
              data-c={rowId}
              data-d={absolute}
              tabIndex={-1}
              aria-hidden="true"
              draggable={false}
            />
          ) : (
            <span
              key={absolute}
              className={cls}
              style={style}
              data-c={rowId}
              data-d={absolute}
              aria-hidden="true"
            />
          );
        })}
      </div>
      {events.length > 0 && (
        <VisuallyHidden as="ul">
          {events.map((event) => (
            <li key={event.key}>{event.text}</li>
          ))}
        </VisuallyHidden>
      )}
    </div>
  );
});
